import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { selectAllergens, selectAppState, selectOpenedDetailDishTypeId } from "slices/appStateSlice";
import {
	selectActualSpotlightDishTypeIds,
	selectAvailableDishTypesIds,
	selectFilteredDishTypeIds,
} from "slices/dishesSlice";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import { selectActiveLocalTransaction } from "slices/transactionSlice";
import { LightingModeType, TransactionState } from "types";
import { AppState } from "types/mainTypes";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { computeDesiredLighting } from "utils/lightingConvergence";
import { createDefaultLight, createSpotlightLight } from "utils/lightingUtils";
import { useFridgeInformation } from "./useFridgeInformation";
import { useAppSelector } from "./utilsHooks/storeHooks";

// How long `appState` must stay IDLE before the lights are allowed to dim to StandBy.
const STANDBY_IDLE_DEBOUNCE_MS = 5000;

// react-idle-timer presence flaps IDLE↔ACTIVE, and dimming to StandBy on every idle blip made the
// fridge lights visibly blink. Gate the IDLE→StandBy transition behind a sustained-idle timer: any
// activity brightens immediately, but the lights only dim once idle has held for
// STANDBY_IDLE_DEBOUNCE_MS. Scoped to the lighting decision so other AppState.IDLE consumers
// (drawer / filter-modal auto-close) keep reacting to idle promptly.
const useDimGatedAppState = (appState: AppState, debounceMs: number): AppState => {
	// Init to the current appState: the debounce applies to active→idle TRANSITIONS (the flap), not
	// to the initial mount. If the app mounts already idle, dimming straight away is correct — it is
	// not a flap.
	const [gatedAppState, setGatedAppState] = useState<AppState>(appState);

	useEffect(() => {
		if (appState !== AppState.IDLE) {
			setGatedAppState(appState); // brighten immediately on any activity
			return;
		}
		const timer = setTimeout(() => setGatedAppState(AppState.IDLE), debounceMs);
		return () => {
			clearTimeout(timer);
		};
	}, [appState, debounceMs]);

	return gatedAppState;
};

// `standbyIdleDebounceMs` is injectable so tests can disable the debounce (0 = dim immediately);
// production callers use the default sustained-idle window.
export const useConvergeLighting = (standbyIdleDebounceMs: number = STANDBY_IDLE_DEBOUNCE_MS) => {
	const { id: fridgeId } = useFridgeInformation();
	// `loading` is a secondary suppression while a mutation is genuinely in flight; the
	// optimistic refs below are the primary guard against subscription-echo re-fires.
	const [setMode, { loading: modeLoading }] = useMutation(SWITCH_FRIDGE_MODE_MUTATION);
	const [setSpotlight, { loading: spotlightLoading }] = useMutation(TOGGLE_SPOTLIGHT_MODE_MUTATION);

	const appState = useAppSelector(selectAppState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const allergens = useAppSelector(selectAllergens);
	const filteredDishTypeIds = useAppSelector(selectFilteredDishTypeIds);
	const actualMode = useAppSelector(selectFridgeLightingMode);
	const actualSpotlightDishTypeIds = useAppSelector(selectActualSpotlightDishTypeIds);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);

	const hasTransaction = transaction != null;
	const transactionState = transaction?.state ?? null;

	// Debounced view of appState for the dim decision (see useDimGatedAppState).
	const appStateForLighting = useDimGatedAppState(appState, standbyIdleDebounceMs);

	// Post-order hand-off: once a transaction reaches Finished, keep deferring to the BE even after the
	// transaction object is cleared (finishOrderState), so the converger does not snap back to ACTIVE
	// and fight the BE's post-order StandBy. The FE resumes control when a new transaction starts, or
	// once the customer leaves (appState IDLE → back to the normal welcome flow).
	const [inPostOrderHandoff, setInPostOrderHandoff] = useState(false);
	useEffect(() => {
		if (transactionState === TransactionState.Finished) {
			setInPostOrderHandoff(true);
		} else if (hasTransaction) {
			setInPostOrderHandoff(false);
		} else if (appState === AppState.IDLE) {
			setInPostOrderHandoff(false);
		}
	}, [transactionState, hasTransaction, appState]);

	// "What we last sent and are still waiting for the BE to echo back." Written SYNCHRONOUSLY
	// before each mutation so that a re-run of this effect inside the echo round-trip — the BE
	// streams many subscription frames while e.g. the fridge door is left open during an order —
	// does NOT re-fire the identical mutation. The refs survive be-authoritative/StandBy dips
	// (cleared only when the BE echo catches up), so a finished transaction or an idle dip does not
	// trigger a redundant resend; only a genuinely new target fires.
	const sentModeRef = useRef<LightingModeType | null>(null);
	const sentSpotlightRef = useRef<readonly string[] | null>(null);

	const desired = useMemo(
		() =>
			computeDesiredLighting({
				appState: appStateForLighting,
				hasTransaction: hasTransaction,
				transactionState: transactionState,
				inPostOrderHandoff: inPostOrderHandoff,
				openDetailDishTypeId: openDetailDishTypeId,
				allergens: allergens,
				filteredDishTypeIds: filteredDishTypeIds,
			}),
		[
			appStateForLighting,
			hasTransaction,
			transactionState,
			inPostOrderHandoff,
			openDetailDishTypeId,
			allergens,
			filteredDishTypeIds,
		],
	);

	useEffect(() => {
		const isMaster = getTabletType() === TabletTypeEnum.Master;
		if (!isMaster) {
			return;
		}
		if (fridgeId == null) {
			return;
		}

		if (desired.kind === "be-authoritative") {
			// The BE owns lighting while a transaction is Finished. Do NOT clear the optimistic
			// sent-refs here: clearing them made the next fe-driven tick re-issue an IDENTICAL command
			// (redundant mutation spam). The refs are still reconciled against the BE echo (echo-match,
			// below) once control returns, so a genuine divergence after Finished still fires; only
			// verbatim repeats are suppressed.
			return;
		}

		// Clear the pending mode marker once the echo has caught up, so it can never wedge the
		// next genuine transition.
		if (sentModeRef.current != null && actualMode === sentModeRef.current) {
			sentModeRef.current = null;
		}

		const modeDiverges = actualMode == null || desired.mode !== actualMode;
		// Suppress only a repeat send of the SAME target we are already awaiting an echo for (or
		// while the mutation is genuinely in flight). A change to a NEW target is never blocked.
		const modeAlreadyInFlight = sentModeRef.current === desired.mode || modeLoading;

		if (modeDiverges && !modeAlreadyInFlight) {
			sentModeRef.current = desired.mode; // optimistic write BEFORE the mutation
			if (shouldTraceLightingDebug()) {
				// TEMP: surfaces THIS tablet's outgoing mode command so the overlay can show whether we
				// are one side of a STAND_BY↔ACTIVE command fight (vs a second master tablet / the BE).
				debugTrace(`[state-debug] FIRE-SWITCH→${desired.mode} (actual=${actualMode})`);
			}
			setMode({
				variables: {
					input: {
						id: fridgeId,
						lighting: {
							mode: desired.mode,
						},
					},
				},
			});
		}

		// Per-dish spotlight reconciliation is only meaningful in Active mode. In StandBy
		// the fridge ignores per-dish state, and on the current BE sending a per-dish
		// payload wakes the fridge back to Active — undoing the StandBy command. Skip the send,
		// but do NOT clear sentSpotlightRef: keeping it across the StandBy dip stops the return to
		// Active (e.g. an idle→active presence flap) from re-issuing an identical TOGGLE.
		if (desired.mode !== LightingModeType.Active) {
			return;
		}

		// Skip spotlight mutation if there is no per-dish payload to build —
		// even when actualSpotlightDishTypeIds is null (cold start), sending
		// an empty payload is pointless.
		if (availableDishTypeIds.size === 0) {
			return;
		}

		// Clear the pending spotlight marker once the echo matches what we sent.
		if (
			sentSpotlightRef.current != null &&
			actualSpotlightDishTypeIds != null &&
			shallowEqual(sentSpotlightRef.current, actualSpotlightDishTypeIds)
		) {
			sentSpotlightRef.current = null;
		}

		const spotlightDiverges =
			actualSpotlightDishTypeIds == null || !shallowEqual(desired.spotlightDishTypeIds, actualSpotlightDishTypeIds);
		// Same in-flight de-dupe as the mode channel: don't re-send the identical per-dish payload
		// while the previous one is still awaiting its echo.
		const spotlightAlreadyInFlight =
			(sentSpotlightRef.current != null && shallowEqual(sentSpotlightRef.current, desired.spotlightDishTypeIds)) ||
			spotlightLoading;

		if (spotlightDiverges && !spotlightAlreadyInFlight) {
			sentSpotlightRef.current = desired.spotlightDishTypeIds; // optimistic write BEFORE the mutation
			const desiredSet = new Set(desired.spotlightDishTypeIds);
			const types = Array.from(availableDishTypeIds).map((id) =>
				desiredSet.has(id) ? createSpotlightLight(id) : createDefaultLight(id),
			);
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] FIRE-TOGGLE target=[${desired.spotlightDishTypeIds.join(",")}]`);
			}
			setSpotlight({
				variables: {
					input: {
						id: fridgeId,
						dishes: {
							types: types,
						},
					},
				},
			});
		}
	}, [
		desired,
		actualMode,
		actualSpotlightDishTypeIds,
		availableDishTypeIds,
		fridgeId,
		setMode,
		setSpotlight,
		modeLoading,
		spotlightLoading,
	]);
};
